import React, { useEffect, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { STATE_TRANSITION_QUERY } from './queries';
import { DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY } from '../../allowedStates/list/queries';
import { DOCUMENT_TYPE_WITH_STATE_TRANSITIONS_QUERY } from '../list/queries';
import { SAVE_DOCUMENT_STATE_TRANSITION } from './mutations';
import { DOCUMENT_TYPE_STATE_TRANSITION_SAVED } from './subscriptions';
import StateTransitionComponent from './StateTransitionComponent';
import { useValidation } from '@totalsoft/pure-validations-react';
import { stateTransitionValidator } from 'features/documentTypes/validators';
import { isValid } from '@totalsoft/pure-validations-react';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Card, FakeText } from '@totalsoft/rocket-ui';
import { ServiceContext } from 'providers/ServiceProvider';
import { useHistory } from 'react-router-dom';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { useToast } from 'hooks/toasts/useToast';
import { TEXT_RESOURCE_LIST_QUERY } from 'features/textResource/queries';
import { Insurance } from 'constants/contexts';

const StateTransitionContainer = ({ stateTransitionId, onChangedStateTransitionTitle, onSaveFnChanged, documentTypeId, onRedirect }) => {
  const [transitionEdit, setTransitionEdit] = useState({ changed: false });
  const [disabledSave, setDisabledSave] = useState(true);
  const [documentStateList, setDocumentStateList] = useState([]);
  const [textResourceCodeList, setTextResourceCodeList] = useState([]);
  const [stateTransitionValidation, validateStateTransition] = useValidation(stateTransitionValidator);
  const [transitionsCheck, setTransitionsCheck] = useState({
    initialTransitionExists: false,
    finalTransitionExists: false,
    sameTransitionExists: false
  });
  const [userGroups, setUserGroups] = useState([]);

  const history = useHistory();
  const [activeService] = useContext(ServiceContext);
  const addToast = useToast();
  const { t } = useTranslation();
  const client = useApolloClient();

  const { loading } = useQuery(STATE_TRANSITION_QUERY, {
    onCompleted: data => {
      setTransitionEdit({ ...data.stateTransition, changed: false });
    },
    variables: {
      id: stateTransitionId
    },
    notifyOnNetworkStatusChange: true,
    skip: !stateTransitionId
  });

  useQuery(DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY, {
    onCompleted: data => {
      setDocumentStateList(
        data?.documentTypeWithAllowedStates?.allowedStates.map(x => {
          return { id: x.documentStateId, name: x.documentStateName };
        })
      );
    },
    variables: {
      id: documentTypeId
    },
    notifyOnNetworkStatusChange: true
  });

  const { data: documentTypeWithStateTransitionsData } = useQuery(DOCUMENT_TYPE_WITH_STATE_TRANSITIONS_QUERY, {
    variables: {
      id: documentTypeId
    },
    notifyOnNetworkStatusChange: true
  });

  useQuery(USER_GROUPS_QUERY, {
    onCompleted: data => {
      setUserGroups(data?.userGroupsConfig);
    }
  });

  useQuery(TEXT_RESOURCE_LIST_QUERY, {
    onCompleted: data => {
      setTextResourceCodeList(
        data?.textResources.map(x => {
          return { id: x.id, code: x.textResourceCode };
        })
      );
    },
    notifyOnNetworkStatusChange: true,
    skip: activeService?.displayName === Insurance.displayName
  });

  const handleUserGroupSelectionChange = value => {
    setTransitionEdit({
      ...transitionEdit,
      userGroupsNotification: value.map(x => ({
        documentAllowedTransitionId: transitionEdit.id,
        userGroupId: x
      })),
      changed: true
    });
    setDisabledSave(false);
  };

  const [saveDocumentStateTransition] = useMutation(SAVE_DOCUMENT_STATE_TRANSITION, {
    onError: () => {
      setDisabledSave(false);
    }
  });

  useSubscription(DOCUMENT_TYPE_STATE_TRANSITION_SAVED, {
    onData: data => {
      addToast(t('DocumentTypes.DocumentStateTransition.DocumentStateTransitionSaved'), 'success');
      setDisabledSave(true);
      if (!stateTransitionId) {
        onRedirect(data.data.data.documentTypeStateTransitionSaved.id);
        client.cache.evict({ id: `DocumentTypeWithStateTransitions:${documentTypeId}` });
        client.cache.gc();
      } else {
        client.cache.evict({ id: `StateTransition:${stateTransitionId}` });
        client.cache.evict({ id: `DocumentTypeWithStateTransitions:${documentTypeId}` });
      }
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'documentTypeWithStateTransitions' });
      client.cache.gc();
    }
  });

  useEffect(() => {
    const stateTransitions = documentTypeWithStateTransitionsData?.documentTypeWithStateTransitions?.stateTransitions;

    if (!stateTransitions) {
      setTransitionsCheck({
        initialTransitionExists: false,
        finalTransitionExists: false,
        sameTransitionExists: false
      });
      return;
    }

    const initialTransitionExists = Boolean(stateTransitions.find(tran => tran.isInitial && tran.id !== transitionEdit.id));
    const finalTransitionExists = Boolean(stateTransitions.find(tran => tran.isFinal && tran.id !== transitionEdit.id));
    const sameTransitionExists = stateTransitions.some(
      tran =>
        tran.fromStateId === transitionEdit.fromStateId && tran.toStateId === transitionEdit.toStateId && tran.id !== transitionEdit.id
    );
    if (sameTransitionExists) setDisabledSave(true);

    setTransitionsCheck({ initialTransitionExists, finalTransitionExists, sameTransitionExists });
  }, [documentTypeWithStateTransitionsData, transitionEdit]);

  const onTransitionPropertyChanged = useCallback(
    (propName, value) => {
      if (propName === 'isInitial') setTransitionEdit({ ...transitionEdit, [propName]: value, fromStateId: null, changed: true });
      else if (propName === 'isFinal') setTransitionEdit({ ...transitionEdit, [propName]: value, toStateId: null, changed: true });
      else setTransitionEdit({ ...transitionEdit, [propName]: value, changed: true });

      setDisabledSave(false);
    },
    [transitionEdit]
  );

  const onSave = useCallback(() => {
    if (!validateStateTransition(transitionEdit)) return;
    saveDocumentStateTransition({
      variables: {
        documentStateTransition: {
          id: transitionEdit.id,
          documentTypeId: transitionEdit.documentTypeId || documentTypeId,
          fromStateId: transitionEdit.fromStateId,
          toStateId: transitionEdit.toStateId,
          saveImage: transitionEdit.saveImage || false,
          requiresConfirmation: transitionEdit.requiresConfirmation || false,
          notifyResponsible: transitionEdit.notifyResponsible || false,
          notifyUser: transitionEdit.notifyUser || false,
          userGroupsNotification: transitionEdit.userGroupsNotification,
          textResourceId: transitionEdit?.textResourceId
        }
      }
    });
    setDisabledSave(true);
    setTransitionEdit({ ...transitionEdit, changed: false });
  }, [documentTypeId, saveDocumentStateTransition, transitionEdit, validateStateTransition]);

  useEffect(() => {
    if (!history) return;
    onChangedStateTransitionTitle(history.location.state?.documentTypeName);
  }, [history, onChangedStateTransitionTitle]);

  useEffect(() => {
    onSaveFnChanged(onSave, disabledSave || !(stateTransitionValidation |> isValid));
  }, [onSaveFnChanged, onSave, stateTransitionValidation, disabledSave]);

  useEffect(() => {
    validateStateTransition(transitionEdit);
  }, [transitionEdit, validateStateTransition]);

  return (
    <>
      {loading || (stateTransitionId && !transitionEdit.id) ? (
        <Card>
          <FakeText lines={10} />
        </Card>
      ) : (
        <StateTransitionComponent
          stateTransition={transitionEdit}
          transitionsCheck={transitionsCheck}
          documentStateList={documentStateList}
          textResourceCodeList={textResourceCodeList}
          userGroups={userGroups}
          onPropertyChanged={onTransitionPropertyChanged}
          onUserGroupChanged={handleUserGroupSelectionChange}
          validation={stateTransitionValidation}
        />
      )}
    </>
  );
};

StateTransitionContainer.propTypes = {
  stateTransitionId: PropTypes.number,
  onChangedStateTransitionTitle: PropTypes.func.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired,
  documentTypeId: PropTypes.number.isRequired,
  stateTransitions: PropTypes.array,
  onRedirect: PropTypes.func.isRequired
};

export default StateTransitionContainer;
